import { ROLES, type Rol, type UsuarioPublic } from '@app/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronsUpDown, KeyRound, UserPlus, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { usuariosApi } from '../api/usuarios.api';
import { UsuarioCrearDialog } from '../components/UsuarioCrearDialog';
import { UsuarioEditarDialog } from '../components/UsuarioEditarDialog';
import { UsuarioResetPasswordDialog } from '../components/UsuarioResetPasswordDialog';

// Sprint 10 — La pantalla de Usuarios adopta la misma representación que el
// listado de A3 (S9-4) y Áreas: barra de filtros + encabezados ordenables +
// tarjetas con relieve. La fila entera abre la edición; el reset de contraseña
// es un botón propio dentro de la tarjeta (stopPropagation).

// Grilla compartida header ↔ tarjetas para alinear columnas.
const GRID_COLS = 'grid-cols-[1.4fr_1.2fr_9rem_12rem_3rem]';

type SortCol = 'email' | 'nombre' | 'rol' | 'estado';
type SortDir = 'asc' | 'desc';
type FiltroEstado = 'todos' | 'activos' | 'inactivos';
const ROL_TODOS = 'TODOS';

export function UsuariosPage(): JSX.Element {
  const [crearOpen, setCrearOpen] = useState(false);
  const [editar, setEditar] = useState<UsuarioPublic | null>(null);
  const [reset, setReset] = useState<UsuarioPublic | null>(null);
  const [filtroEmail, setFiltroEmail] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroRol, setFiltroRol] = useState<Rol | typeof ROL_TODOS>(ROL_TODOS);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('todos');
  const [sortBy, setSortBy] = useState<SortCol>('nombre');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const es = useI18n();

  const { data, isLoading, error } = useQuery({
    queryKey: qk.usuarios.list({ take: 50 }),
    queryFn: () => usuariosApi.list({ take: 50 }),
  });

  const hayFiltros =
    filtroEmail.trim() !== '' ||
    filtroNombre.trim() !== '' ||
    filtroRol !== ROL_TODOS ||
    filtroEstado !== 'todos';

  const items = useMemo(() => {
    if (!data) return [];
    const email = filtroEmail.trim().toLowerCase();
    const nombre = filtroNombre.trim().toLowerCase();
    const filtrados = data.usuarios.filter((u) => {
      if (email && !u.email.toLowerCase().includes(email)) return false;
      if (nombre && !u.nombre.toLowerCase().includes(nombre)) return false;
      if (filtroRol !== ROL_TODOS && u.rol !== filtroRol) return false;
      if (filtroEstado === 'activos' && !u.activo) return false;
      if (filtroEstado === 'inactivos' && u.activo) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtrados].sort((a, b) => {
      if (sortBy === 'estado' && a.activo !== b.activo) {
        return ((a.activo ? 1 : 0) - (b.activo ? 1 : 0)) * dir;
      }
      if (sortBy === 'rol' && a.rol !== b.rol) {
        return es.roles[a.rol].localeCompare(es.roles[b.rol], 'es') * dir;
      }
      if (sortBy === 'email') return a.email.localeCompare(b.email, 'es') * dir;
      // Orden por nombre (criterio principal o desempate).
      return a.nombre.localeCompare(b.nombre, 'es') * dir;
    });
  }, [data, filtroEmail, filtroNombre, filtroRol, filtroEstado, sortBy, sortDir, es]);

  function toggleSort(col: SortCol): void {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(col);
      setSortDir('asc');
    }
  }

  function limpiar(): void {
    setFiltroEmail('');
    setFiltroNombre('');
    setFiltroRol(ROL_TODOS);
    setFiltroEstado('todos');
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-titulos text-3xl font-bold text-text-marca">{es.admin.usuarios.titulo}</h1>
        <Button onClick={() => setCrearOpen(true)}>
          <UserPlus size={16} />
          {es.admin.usuarios.crear}
        </Button>
      </div>

      {/* Barra de filtros por columna — alineada con la representación del
          listado de A3: una entrada de filtro por cada columna. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={filtroEmail}
          onChange={(e) => setFiltroEmail(e.target.value)}
          placeholder={es.admin.usuarios.filtroEmailPlaceholder}
          className="h-9 w-56"
        />
        <Input
          value={filtroNombre}
          onChange={(e) => setFiltroNombre(e.target.value)}
          placeholder={es.admin.usuarios.filtroNombrePlaceholder}
          className="h-9 w-56"
        />
        <Select value={filtroRol} onValueChange={(v) => setFiltroRol(v as Rol | typeof ROL_TODOS)}>
          <SelectTrigger className="h-9 w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ROL_TODOS}>{es.admin.usuarios.filtroRolTodos}</SelectItem>
            {ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {es.roles[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtroEstado} onValueChange={(v) => setFiltroEstado(v as FiltroEstado)}>
          <SelectTrigger className="h-9 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">{es.admin.usuarios.filtroEstado.todos}</SelectItem>
            <SelectItem value="activos">{es.admin.usuarios.filtroEstado.activos}</SelectItem>
            <SelectItem value="inactivos">{es.admin.usuarios.filtroEstado.inactivos}</SelectItem>
          </SelectContent>
        </Select>
        {hayFiltros && (
          <Button variant="ghost" size="sm" onClick={limpiar}>
            <X size={14} />
            {es.admin.usuarios.limpiar}
          </Button>
        )}
      </div>

      {error && <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>}

      {isLoading && <p className="text-sm text-text-subtle">{es.admin.usuarios.cargando}</p>}

      {data && (
        <>
          {/* Encabezados con ordenamiento — alineados con las columnas de las tarjetas. */}
          {items.length > 0 && (
            <div className={cn('mb-2 hidden px-5 md:grid', GRID_COLS, 'items-center gap-4')}>
              <ColHeader
                col="email"
                label={es.admin.usuarios.col.email}
                {...{ sortBy, sortDir, onSort: toggleSort }}
              />
              <ColHeader
                col="nombre"
                label={es.admin.usuarios.col.nombre}
                {...{ sortBy, sortDir, onSort: toggleSort }}
              />
              <ColHeader
                col="rol"
                label={es.admin.usuarios.col.rol}
                {...{ sortBy, sortDir, onSort: toggleSort }}
              />
              <ColHeader
                col="estado"
                label={es.admin.usuarios.col.activo}
                {...{ sortBy, sortDir, onSort: toggleSort }}
              />
              <span className="text-right text-[11px] font-semibold uppercase tracking-wide text-text-subtle">
                {es.admin.usuarios.col.acciones}
              </span>
            </div>
          )}

          {/* Tarjetas con relieve — la fila entera abre la edición. */}
          <div className="flex flex-col gap-2">
            {items.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => setEditar(u)}
                aria-label={`${es.admin.usuarios.col.editar} ${u.email}`}
                className={cn(
                  'grid gap-x-4 gap-y-1 rounded-xl border border-border-subtle bg-surface px-5 py-3.5 text-left shadow-card',
                  'transition-[box-shadow,border-color] duration-200 ease-out hover:border-marca-primario/25 hover:shadow-card-hover',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-marca-primario/60',
                  'md:items-center',
                  GRID_COLS,
                )}
              >
                <span className="truncate text-sm text-text-subtle">{u.email}</span>
                <span className="truncate text-text-strong">{u.nombre}</span>
                <span className="truncate text-sm text-text-subtle">{es.roles[u.rol]}</span>
                <span className="flex flex-wrap items-center gap-1">
                  {u.activo ? (
                    <span className="rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                      {es.admin.usuarios.estado.activo}
                    </span>
                  ) : (
                    <span className="rounded bg-text-faint/10 px-2 py-0.5 text-xs font-medium text-text-subtle">
                      {es.admin.usuarios.estado.inactivo}
                    </span>
                  )}
                  {u.debeCambiarPassword && (
                    <span className="rounded bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                      {es.admin.usuarios.estado.debeCambiarPassword}
                    </span>
                  )}
                </span>
                <span className="flex justify-end">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setReset(u);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setReset(u);
                      }
                    }}
                    title={`${es.admin.usuarios.col.resetPassword} ${u.email}`}
                    aria-label={`${es.admin.usuarios.col.resetPassword} ${u.email}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-faint hover:bg-surface-muted hover:text-text-marca"
                  >
                    <KeyRound size={15} />
                  </span>
                </span>
              </button>
            ))}
            {items.length === 0 && (
              <div className="rounded-lg border border-border-subtle bg-surface px-4 py-10 text-center text-text-subtle shadow-card">
                {hayFiltros ? es.admin.usuarios.sinResultados : es.admin.usuarios.vacio}
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-text-faint">
            {data.total} {data.total === 1 ? es.admin.usuarios.totalSingular : es.admin.usuarios.totalPlural}
          </p>
        </>
      )}

      <UsuarioCrearDialog open={crearOpen} onOpenChange={setCrearOpen} />
      {editar && <UsuarioEditarDialog usuario={editar} open onOpenChange={(o) => !o && setEditar(null)} />}
      {reset && (
        <UsuarioResetPasswordDialog usuario={reset} open onOpenChange={(o) => !o && setReset(null)} />
      )}
    </div>
  );
}

function ColHeader({
  col,
  label,
  sortBy,
  sortDir,
  onSort,
}: {
  col: SortCol;
  label: string;
  sortBy: SortCol;
  sortDir: SortDir;
  onSort: (c: SortCol) => void;
}): JSX.Element {
  const activo = sortBy === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={cn(
        'flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide hover:text-text-marca',
        activo ? 'text-text-marca' : 'text-text-subtle',
      )}
    >
      {label}
      {activo ? (
        sortDir === 'asc' ? (
          <ArrowUp size={12} />
        ) : (
          <ArrowDown size={12} />
        )
      ) : (
        <ChevronsUpDown size={12} className="text-text-faint" />
      )}
    </button>
  );
}
